import React, { useState, useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from "@/components/ui/button";
import { useNavigate } from 'react-router-dom';
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import FrequentlyAskedQuestions from '@/components/shared/FrequentlyAskedQuestions';
import ProvenResults from '@/components/shared/ProvenResults';
import SEO from '@/components/shared/SEO';

// Define the ATS integration type
interface ATSIntegration {
  id: string;
  name: string;
  logo: string;
  category: string;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  subject: string;
  message: string;
}

const ATSIntegrations = () => {
  const navigate = useNavigate();
  const [integrations, setIntegrations] = useState<ATSIntegration[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    subject: 'New ATS Integration Request',
    message: ''
  });

  useEffect(() => {
    const loadIntegrations = async () => {
      try {
        // Check for cached list in localStorage
        const cached = localStorage.getItem('roundone_ats_list');
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (parsed.success && parsed.data) {
              const atsApps = parsed.data.map((app: any) => ({
                id: app.id,
                name: app.id === 'teamtailor' ? 'Team Tailor' : app.label,
                logo: app.logo_short || `/ats-logos/${app.id}.png`,
                category: app.category
              }));
              setIntegrations(atsApps);
            }
          } catch (_) {
            // If parsing cache fails, ignore and fetch fresh
          }
        }

        // Always attempt to fetch fresh data to keep list up-to-date
        const response = await fetch('/ats-logos/atslist.json', { cache: 'no-cache' });
        if (!response.ok) {
          throw new Error('Failed to fetch ATS list');
        }

        const data = await response.json();
        if (data.success && data.data) {
          localStorage.setItem('roundone_ats_list', JSON.stringify(data));
          const atsApps = data.data.map((app: any) => ({
            id: app.id,
            name: app.id === 'teamtailor' ? 'Team Tailor' : app.label,
            logo: app.logo_short || `/ats-logos/${app.id}.png`,
            category: app.category
          }));
          setIntegrations(atsApps);
        }
      } catch (error) {
        console.error('Error loading ATS integrations:', error);
      } finally {
        setLoading(false);
      }
    };

    loadIntegrations();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id.replace('first-name', 'firstName').replace('last-name', 'lastName')]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      // Send the email using Supabase Edge Function
      const { data, error } = await supabase.functions.invoke('direct-email', {
        body: {
          to: 'mayur@roundone.ai',
          templateAlias: 'ats-integration-request',
          templateModel: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            message: formData.message
          },
          from: 'hello@roundone.ai',
          replyTo: formData.email
        }
      });

      if (error) {
        throw new Error(error.message || 'Failed to send message');
      }

      // Reset form data after successful submission
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        subject: 'New ATS Integration Request',
        message: ''
      });

      toast.success('Your message has been sent successfully!');
    } catch (error) {
      console.error('Error sending contact form:', error);
      toast.error(`Failed to send your message. Please try again later. ${error instanceof Error ? error.message : ''}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Create ATS integrations structured data
  const atsIntegrationsSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "ATS Integrations - RoundOne AI",
    "description": "Connect RoundOne AI with your favorite Applicant Tracking Systems. Seamless integration with leading ATS platforms for streamlined hiring.",
    "url": "https://roundone.ai/ats-integrations",
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": ["h1", "h2", "h3"]
    }
  };

  // Create software application structured data
  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "RoundOne AI Assessment Platform",
    "applicationCategory": "BusinessApplication",
    "applicationSubCategory": "HR Tools",
    "operatingSystem": "Web-based",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "GBP"
    },
    "featureList": "ATS Integration, AI Interviewing, Candidate Assessment",
    "screenshot": "https://roundone.ai/images/dashboard-screenshot.png"
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEO 
        title="ATS Integrations - Connect Your Hiring Tools"
        description="Seamlessly integrate RoundOne AI with your existing Applicant Tracking System. Two-click connection with leading ATS platforms for a unified hiring workflow."
        url="/ats-integrations"
        schema={[atsIntegrationsSchema, softwareSchema]}
      />
      
      <Header />
      
      <main className="flex-1">
        {/* Hero section */}
        <section className="bg-roundone-dark-purple py-16 md:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl font-bold mb-6 text-white">Seamless ATS Integration</h1>
              <p className="text-lg text-roundone-white/80 mb-8">
                RoundOne integrates with your favorite Applicant Tracking Systems to streamline your hiring process.
                No manual data entry, no switching between platforms.
              </p>
              <Button 
                className="gradient-button py-6 px-8 text-lg rounded-full" 
                onClick={() => navigate('/')}
              >
                Get Started
              </Button>
            </div>
          </div>
        </section>

        {/* Benefits section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Why Integrate Your ATS?</h2>
              <p className="text-gray-600">
                Streamline your hiring process and save time with our seamless ATS integrations.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-semibold mb-3">Automated Data Sync</h3>
                <p className="text-gray-600">
                  Candidate data flows automatically between RoundOne and your ATS, eliminating manual data entry.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-semibold mb-3">Unified Workflow</h3>
                <p className="text-gray-600">
                  Manage your entire hiring process from a single platform without switching between tools.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-semibold mb-3">Real-time Updates</h3>
                <p className="text-gray-600">
                  Assessment results and candidate data are synced in real-time to your ATS system.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Integrations Grid */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Supported ATS Platforms</h2>
              <p className="text-gray-600">
                We integrate with leading Applicant Tracking Systems to provide a seamless experience.
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {loading ? (
                // Loading skeleton
                Array(8).fill(null).map((_, index) => (
                  <div key={index} className="aspect-square bg-gray-100 rounded-lg animate-pulse" />
                ))
              ) : (
                // Actual integrations
                integrations.map(integration => (
                  <div
                    key={integration.id}
                    className="aspect-square bg-white border border-gray-200 rounded-lg p-6 flex flex-col items-center justify-center hover:border-roundone-purple/30 hover:shadow-sm transition-all duration-200"
                  >
                    <img
                      src={integration.logo}
                      alt={`${integration.name} logo`}
                      className="w-16 h-16 object-contain mb-4"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/ats-logos/default.png';
                      }}
                    />
                    <h3 className="text-center font-medium">{integration.name}</h3>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-md mx-auto">
              <h2 className="text-2xl font-bold mb-6 text-center">Request New ATS Integration</h2>
              <p className="text-gray-600 text-center mb-8">
                Don't see your ATS listed? Fill out this form to request integration with your preferred ATS platform.
              </p>
              
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="first-name" className="block text-sm font-medium text-gray-700">
                      First name
                    </label>
                    <input
                      type="text"
                      id="first-name"
                      value={formData.firstName}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-roundone-purple focus:outline-none focus:ring-roundone-purple sm:text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="last-name" className="block text-sm font-medium text-gray-700">
                      Last name
                    </label>
                    <input
                      type="text"
                      id="last-name"
                      value={formData.lastName}
                      onChange={handleChange}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-roundone-purple focus:outline-none focus:ring-roundone-purple sm:text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-roundone-purple focus:outline-none focus:ring-roundone-purple sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                    Subject
                  </label>
                  <input
                    type="text"
                    id="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-roundone-purple focus:outline-none focus:ring-roundone-purple sm:text-sm bg-gray-50"
                    readOnly
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                    Message
                  </label>
                  <textarea
                    id="message"
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Please provide details about the ATS you'd like us to integrate with (name, website, etc.)"
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-roundone-purple focus:outline-none focus:ring-roundone-purple sm:text-sm"
                  />
                </div>
                <div>
                  <Button 
                    type="submit"
                    className="gradient-button w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Request'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </section>

        {/* Add FrequentlyAskedQuestions component */}
        <FrequentlyAskedQuestions />
        
        {/* Add ProvenResults component */}
        <ProvenResults />
      </main>
      
      <Footer />
    </div>
  );
};

export default ATSIntegrations; 